import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Sparkles, 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  RefreshCw,
  ShoppingCart,
  Heart,
  TrendingUp,
  Eye,
  Link2,
  Unlink,
  Box,
  Search,
  Wand2,
  ArrowRight,
  CheckCircle
} from "lucide-react";

const API = "/api";

export default function CombinationsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Kombinler
  const [combinations, setCombinations] = useState([]);
  const [filteredCombinations, setFilteredCombinations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Products
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({
    enableCrossSell: true,
    enableUpsell: true,
    maxRecommendations: 4,
    autoGenerateFromOrders: false,
    showOnProductPage: true,
    showOnCartPage: true,
    displayStyle: "grid" // grid, carousel, list
  });
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [comboForm, setComboForm] = useState({
    name: "",
    type: "outfit", // outfit, frequently_bought, upsell, cross_sell
    sourceProduct: null,
    relatedProducts: [],
    discountPercent: 0,
    isActive: true,
    priority: 1
  });
  
  // Product selection
  const [selectingFor, setSelectingFor] = useState(null); // 'source' or 'related'
  const [productSelectOpen, setProductSelectOpen] = useState(false);

  useEffect(() => {
    fetchCombinations();
    fetchSettings();
  }, [token]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = combinations.filter(c => 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCombinations(filtered);
    } else {
      setFilteredCombinations(combinations);
    }
  }, [searchTerm, combinations]);

  const fetchCombinations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/combinations?token=${token}`);
      setCombinations(response.data.combinations || []);
    } catch (error) {
      console.error("Combinations fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/combinations?token=${token}`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Settings fetch error:", error);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/nebim/products?token=${token}`);
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error("Ürünler yüklenemedi");
    } finally {
      setLoadingProducts(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/combinations?token=${token}`, settings);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const openAddDialog = () => {
    setEditingCombo(null);
    setComboForm({
      name: "",
      type: "outfit",
      sourceProduct: null,
      relatedProducts: [],
      discountPercent: 0,
      isActive: true,
      priority: 1
    });
    setDialogOpen(true);
    fetchProducts();
  };

  const openEditDialog = (combo) => {
    setEditingCombo(combo);
    setComboForm({
      name: combo.name || "",
      type: combo.type || "outfit",
      sourceProduct: combo.sourceProduct || null,
      relatedProducts: combo.relatedProducts || [],
      discountPercent: combo.discountPercent || 0,
      isActive: combo.isActive !== false,
      priority: combo.priority || 1
    });
    setDialogOpen(true);
    fetchProducts();
  };

  const saveCombo = async () => {
    if (!comboForm.name) {
      toast.error("Kombin adı zorunludur");
      return;
    }
    if (!comboForm.sourceProduct) {
      toast.error("Ana ürün seçmelisiniz");
      return;
    }
    if (comboForm.relatedProducts.length === 0) {
      toast.error("En az 1 ilişkili ürün eklemelisiniz");
      return;
    }

    setSaving(true);
    try {
      if (editingCombo) {
        await axios.put(`${API}/combinations/${editingCombo.id}?token=${token}`, comboForm);
        toast.success("Kombin güncellendi");
      } else {
        await axios.post(`${API}/combinations?token=${token}`, comboForm);
        toast.success("Kombin oluşturuldu");
      }
      setDialogOpen(false);
      fetchCombinations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteCombo = async (comboId) => {
    if (!confirm("Bu kombini silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/combinations/${comboId}?token=${token}`);
      toast.success("Kombin silindi");
      fetchCombinations();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const generateAutoRecommendations = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/combinations/auto-generate?token=${token}`);
      toast.success(`${response.data.generated || 0} yeni kombin önerisi oluşturuldu`);
      fetchCombinations();
    } catch (error) {
      toast.error("Otomatik öneri oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  const selectProduct = (product) => {
    if (selectingFor === 'source') {
      setComboForm({
        ...comboForm,
        sourceProduct: {
          productCode: product.productCode,
          productName: product.productName || product.productCode,
          price: product.price || 0,
          image: product.image || ""
        }
      });
    } else if (selectingFor === 'related') {
      const exists = comboForm.relatedProducts.find(p => p.productCode === product.productCode);
      if (!exists) {
        setComboForm({
          ...comboForm,
          relatedProducts: [...comboForm.relatedProducts, {
            productCode: product.productCode,
            productName: product.productName || product.productCode,
            price: product.price || 0,
            image: product.image || ""
          }]
        });
      }
    }
    setProductSelectOpen(false);
  };

  const removeRelatedProduct = (productCode) => {
    setComboForm({
      ...comboForm,
      relatedProducts: comboForm.relatedProducts.filter(p => p.productCode !== productCode)
    });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "outfit": return "Kombin";
      case "frequently_bought": return "Sık Birlikte Alınan";
      case "upsell": return "Upsell";
      case "cross_sell": return "Cross-sell";
      default: return type;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "outfit": return "bg-pink-500";
      case "frequently_bought": return "bg-blue-500";
      case "upsell": return "bg-purple-500";
      case "cross_sell": return "bg-emerald-500";
      default: return "bg-gray-500";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "outfit": return <Heart className="w-4 h-4" />;
      case "frequently_bought": return <ShoppingCart className="w-4 h-4" />;
      case "upsell": return <TrendingUp className="w-4 h-4" />;
      case "cross_sell": return <Link2 className="w-4 h-4" />;
      default: return <Sparkles className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="combinations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Kombin & Öneriler</h1>
          <p className="text-slate-500 mt-1">Cross-sell, Upsell ve Kombin önerileri yönetimi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={generateAutoRecommendations} disabled={saving}>
            <Wand2 className="w-4 h-4 mr-2" />
            Otomatik Oluştur
          </Button>
          <Button onClick={openAddDialog} className="bg-pink-600 hover:bg-pink-700">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Kombin
          </Button>
        </div>
      </div>

      <Tabs defaultValue="combinations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="combinations" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Kombinler
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Ayarlar
          </TabsTrigger>
        </TabsList>

        {/* Kombinler Tab */}
        <TabsContent value="combinations" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <Heart className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{combinations.filter(c => c.type === 'outfit').length}</p>
                    <p className="text-xs text-slate-500">Kombin</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{combinations.filter(c => c.type === 'frequently_bought').length}</p>
                    <p className="text-xs text-slate-500">Sık Alınan</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{combinations.filter(c => c.type === 'upsell').length}</p>
                    <p className="text-xs text-slate-500">Upsell</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Link2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{combinations.filter(c => c.type === 'cross_sell').length}</p>
                    <p className="text-xs text-slate-500">Cross-sell</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <Card>
            <CardContent className="pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Kombin adı veya türü ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Combinations Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-500" />
                Kombin Listesi
              </CardTitle>
              <CardDescription>{filteredCombinations.length} kombin listeleniyor</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : filteredCombinations.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz kombin oluşturulmadı</p>
                  <Button onClick={openAddDialog} className="mt-4" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Kombini Oluştur
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kombin Adı</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Ana Ürün</TableHead>
                      <TableHead className="text-center">İlişkili Ürün</TableHead>
                      <TableHead className="text-center">İndirim</TableHead>
                      <TableHead className="text-center">Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCombinations.map((combo) => (
                      <TableRow key={combo.id}>
                        <TableCell>
                          <p className="font-medium">{combo.name}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getTypeBadgeColor(combo.type)} flex items-center gap-1 w-fit`}>
                            {getTypeIcon(combo.type)}
                            {getTypeLabel(combo.type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                              <Box className="w-4 h-4 text-slate-400" />
                            </div>
                            <span className="text-sm truncate max-w-[120px]">
                              {combo.sourceProduct?.productName || combo.sourceProduct?.productCode}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{combo.relatedProducts?.length || 0} ürün</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {combo.discountPercent > 0 && (
                            <Badge className="bg-red-500">%{combo.discountPercent}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {combo.isActive ? (
                            <Badge className="bg-emerald-500">Aktif</Badge>
                          ) : (
                            <Badge variant="secondary">Pasif</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openEditDialog(combo)}>
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteCombo(combo.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ayarlar Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Öneri Ayarları</CardTitle>
              <CardDescription>Cross-sell ve Upsell önerilerinin nasıl görüntüleneceğini ayarlayın</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Cross-sell Önerileri</Label>
                    <p className="text-xs text-slate-500">"Bununla birlikte alın" önerileri</p>
                  </div>
                  <Switch
                    checked={settings.enableCrossSell}
                    onCheckedChange={(v) => setSettings({...settings, enableCrossSell: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Upsell Önerileri</Label>
                    <p className="text-xs text-slate-500">"Bunları da beğenebilirsiniz" önerileri</p>
                  </div>
                  <Switch
                    checked={settings.enableUpsell}
                    onCheckedChange={(v) => setSettings({...settings, enableUpsell: v})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Maksimum Öneri Sayısı</Label>
                  <Select 
                    value={settings.maxRecommendations.toString()} 
                    onValueChange={(v) => setSettings({...settings, maxRecommendations: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 ürün</SelectItem>
                      <SelectItem value="3">3 ürün</SelectItem>
                      <SelectItem value="4">4 ürün</SelectItem>
                      <SelectItem value="6">6 ürün</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Görüntüleme Stili</Label>
                  <Select 
                    value={settings.displayStyle} 
                    onValueChange={(v) => setSettings({...settings, displayStyle: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid">Grid (Izgara)</SelectItem>
                      <SelectItem value="carousel">Carousel (Kaydırmalı)</SelectItem>
                      <SelectItem value="list">Liste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <p className="font-medium text-slate-700">Görüntüleme Konumları</p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Ürün Sayfasında Göster</Label>
                      <p className="text-xs text-slate-500">Ürün detay sayfasında önerileri göster</p>
                    </div>
                    <Switch
                      checked={settings.showOnProductPage}
                      onCheckedChange={(v) => setSettings({...settings, showOnProductPage: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Sepet Sayfasında Göster</Label>
                      <p className="text-xs text-slate-500">Sepet sayfasında önerileri göster</p>
                    </div>
                    <Switch
                      checked={settings.showOnCartPage}
                      onCheckedChange={(v) => setSettings({...settings, showOnCartPage: v})}
                    />
                  </div>
                </div>
              </div>

              <Button onClick={saveSettings} disabled={saving}>
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Ayarları Kaydet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Combo Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCombo ? "Kombini Düzenle" : "Yeni Kombin Oluştur"}</DialogTitle>
            <DialogDescription>
              Ürünler arasında ilişki kurarak satışları artırın
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="comboName">Kombin Adı *</Label>
                <Input
                  id="comboName"
                  value={comboForm.name}
                  onChange={(e) => setComboForm({...comboForm, name: e.target.value})}
                  placeholder="Örn: Yaz Kombini"
                />
              </div>
              <div className="space-y-2">
                <Label>Öneri Türü</Label>
                <Select 
                  value={comboForm.type} 
                  onValueChange={(v) => setComboForm({...comboForm, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outfit">Kombin (Outfit)</SelectItem>
                    <SelectItem value="frequently_bought">Sık Birlikte Alınan</SelectItem>
                    <SelectItem value="upsell">Upsell (Daha İyisi)</SelectItem>
                    <SelectItem value="cross_sell">Cross-sell (Tamamlayıcı)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kombin İndirimi (%)</Label>
                <Input
                  type="number"
                  value={comboForm.discountPercent}
                  onChange={(e) => setComboForm({...comboForm, discountPercent: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                />
                <p className="text-xs text-slate-500">Birlikte alındığında uygulanacak indirim</p>
              </div>
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Select 
                  value={comboForm.priority.toString()} 
                  onValueChange={(v) => setComboForm({...comboForm, priority: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - En Yüksek</SelectItem>
                    <SelectItem value="2">2 - Yüksek</SelectItem>
                    <SelectItem value="3">3 - Normal</SelectItem>
                    <SelectItem value="4">4 - Düşük</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ana Ürün Seçimi */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Ana Ürün *</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => { setSelectingFor('source'); setProductSelectOpen(true); fetchProducts(); }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ürün Seç
                </Button>
              </div>
              {comboForm.sourceProduct ? (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                  <div className="w-12 h-12 bg-white rounded flex items-center justify-center">
                    <Box className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{comboForm.sourceProduct.productName}</p>
                    <p className="text-xs text-slate-500">{comboForm.sourceProduct.productCode}</p>
                  </div>
                  <p className="font-mono">{comboForm.sourceProduct.price?.toFixed(2)} ₺</p>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => setComboForm({...comboForm, sourceProduct: null})}
                  >
                    <Unlink className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                  <Box className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Ana ürün seçilmedi</p>
                </div>
              )}
            </div>

            {/* İlişkili Ürünler */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>İlişkili Ürünler ({comboForm.relatedProducts.length})</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => { setSelectingFor('related'); setProductSelectOpen(true); fetchProducts(); }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ürün Ekle
                </Button>
              </div>
              {comboForm.relatedProducts.length > 0 ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {comboForm.relatedProducts.map((product, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 bg-white rounded flex items-center justify-center">
                        <Box className="w-5 h-5 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.productName}</p>
                        <p className="text-xs text-slate-500">{product.productCode}</p>
                      </div>
                      <p className="font-mono text-sm">{product.price?.toFixed(2)} ₺</p>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => removeRelatedProduct(product.productCode)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                  <Link2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">İlişkili ürün eklenmedi</p>
                </div>
              )}
            </div>

            {/* Preview */}
            {comboForm.sourceProduct && comboForm.relatedProducts.length > 0 && (
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-100">
                <p className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Önizleme
                </p>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white rounded-lg mx-auto mb-1 flex items-center justify-center shadow-sm">
                      <Box className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-xs font-medium truncate max-w-[80px]">{comboForm.sourceProduct.productName}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-pink-400" />
                  <div className="flex gap-2">
                    {comboForm.relatedProducts.slice(0, 3).map((p, i) => (
                      <div key={i} className="text-center">
                        <div className="w-12 h-12 bg-white rounded-lg mx-auto mb-1 flex items-center justify-center shadow-sm">
                          <Box className="w-6 h-6 text-slate-400" />
                        </div>
                        <p className="text-xs truncate max-w-[60px]">{p.productName}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Aktif</Label>
                <p className="text-xs text-slate-500">Bu kombini göster</p>
              </div>
              <Switch
                checked={comboForm.isActive}
                onCheckedChange={(v) => setComboForm({...comboForm, isActive: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={saveCombo} disabled={saving} className="bg-pink-600 hover:bg-pink-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingCombo ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog open={productSelectOpen} onOpenChange={setProductSelectOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {selectingFor === 'source' ? 'Ana Ürün Seç' : 'İlişkili Ürün Ekle'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {loadingProducts ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto divide-y">
                {products.slice(0, 50).map((product, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => selectProduct(product)}
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                      <Box className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.productName || product.productCode}</p>
                      <p className="text-xs text-slate-500">{product.productCode}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm">{product.price?.toFixed(2) || "0.00"} ₺</p>
                    </div>
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setProductSelectOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
